import clsx from 'clsx';

export type InsightsSectionTab = {
  id: string;
  label: string;
  count?: number | null;
  /** Per-tab marker (e.g. AI) shown next to the label. */
  badge?: string | null;
};

interface InsightsSectionNavProps {
  tabs: InsightsSectionTab[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Shared NDT-style pill nav for insights panels (red active pill + count badge).
 */
export function InsightsSectionNav({
  tabs,
  activeId,
  onChange,
  className,
  'aria-label': ariaLabel = 'Insights sections',
}: InsightsSectionNavProps) {
  return (
    <div
      className={clsx('flex flex-wrap items-center gap-2 border-b border-slate-200 pb-4', className)}
      role="tablist"
      aria-label={ariaLabel}
      data-testid="insights-section-nav"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={clsx(
              'rounded-lg px-4 py-2 text-sm font-medium transition',
              active
                ? 'border border-brand bg-brand/10 text-brand'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )}
          >
            {tab.label}
            {tab.count != null ? (
              <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-xs">
                {tab.count}
              </span>
            ) : null}
            {tab.badge ? (
              <span
                data-testid="insights-tab-ai-badge"
                className={clsx(
                  'ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                  active
                    ? 'bg-brand/15 text-brand'
                    : 'bg-[rgba(0,39,63,0.08)] text-blueprint-blue',
                )}
              >
                {tab.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
